import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Toggle,
  Badge,
  ErrorBanner,
  formatCents,
} from '../components/ui';

interface Service {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  priceCents: number;
  category?: string;
  color?: string;
  maxBookingsPerSlot: number;
  requiresPayment: boolean;
  depositPercent: number;
  active: boolean;
}

export function ServicesPage() {
  const [editing, setEditing] = useState<Service | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ data: Service[] }>('/services').catch(() => ({ data: [] })),
  });

  const services = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Services"
        subtitle={`${services.length} bookable services`}
        action={<Button onClick={() => setShowCreate(true)}>+ New service</Button>}
      />

      {q.isLoading ? (
        <Spinner />
      ) : services.length === 0 ? (
        <Card>
          <EmptyState
            title="No services yet"
            description="Create services your clients can book."
            action={<Button onClick={() => setShowCreate(true)}>+ New service</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'var(--space-3)' }}>
          {services.map((s) => (
            <Card key={s.id} onClick={() => setEditing(s)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                <div style={{ width: 12, height: 12, background: s.color ?? 'var(--color-brand-primary)', borderRadius: '50%' }} />
                <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 600, flex: 1 }}>{s.name}</h3>
                {!s.active && <Badge color="gray">inactive</Badge>}
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                {s.durationMinutes} min · {formatCents(s.priceCents)}
              </div>
              {s.description && (
                <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                  {s.description}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      {(showCreate || editing) && (
        <ServiceModal
          service={editing}
          onClose={() => {
            setShowCreate(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['services'] });
            setShowCreate(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ServiceModal({
  service,
  onClose,
  onSaved,
}: {
  service: Service | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(service?.name ?? '');
  const [description, setDescription] = useState(service?.description ?? '');
  const [durationMinutes, setDuration] = useState(String(service?.durationMinutes ?? 60));
  const [price, setPrice] = useState(service ? (service.priceCents / 100).toFixed(2) : '0.00');
  const [category, setCategory] = useState(service?.category ?? '');
  const [color, setColor] = useState(service?.color ?? '#3b82f6');
  const [capacity, setCapacity] = useState(String(service?.maxBookingsPerSlot ?? 1));
  const [requiresPayment, setRequiresPayment] = useState(service?.requiresPayment ?? false);
  const [depositPercent, setDeposit] = useState(String(service?.depositPercent ?? 0));
  const [active, setActive] = useState(service?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || undefined,
        durationMinutes: Number(durationMinutes) || 60,
        priceCents: Math.round((Number(price) || 0) * 100),
        category: category || undefined,
        color,
        maxBookingsPerSlot: Number(capacity) || 1,
        requiresPayment,
        depositPercent: Number(depositPercent) || 0,
        active,
      };
      return service
        ? api.patch(`/services/${service.id}`, body)
        : api.post('/services', body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/services/${service!.id}`),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Delete failed'),
  });

  return (
    <Modal onClose={onClose} title={service ? 'Edit service' : 'New service'} width={560}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          saveMut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Name *">
          <Input value={name} onChange={setName} autoFocus />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={setDescription} rows={2} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-2)' }}>
          <Field label="Duration (min)">
            <Input value={durationMinutes} onChange={setDuration} type="number" min={5} max={480} />
          </Field>
          <Field label="Price">
            <Input value={price} onChange={setPrice} type="number" step="0.01" min={0} />
          </Field>
          <Field label="Capacity">
            <Input value={capacity} onChange={setCapacity} type="number" min={1} max={100} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-2)' }}>
          <Field label="Category">
            <Input value={category} onChange={setCategory} />
          </Field>
          <Field label="Color">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{ width: '100%', height: 36, padding: 0, border: '1px solid var(--color-border-primary)', borderRadius: 'var(--radius-md)' }}
            />
          </Field>
          <Field label="Deposit %">
            <Input value={depositPercent} onChange={setDeposit} type="number" min={0} max={100} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
          <Toggle checked={requiresPayment} onChange={setRequiresPayment} label="Requires payment" />
          <Toggle checked={active} onChange={setActive} label="Active" />
        </div>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-2)' }}>
          {service ? (
            <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
